import React, { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Camera, X } from 'lucide-react';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';
import { AuthContext } from '../../contexts/AuthContext';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080';

interface Equipment {
  equipment_id: number;
  equipment_name: string;
  bradanna_equipment_number: string | null;
  hours_last_serviced: number | null;
  status: string | null;
}

type Step = 'scan' | 'sim' | 'pick' | 'hours' | 'confirm' | 'done';

const LinkTracker: React.FC = () => {
  const navigate = useNavigate();
  const auth = useContext(AuthContext);
  const isSuperAdmin = !!auth?.user?.is_super_admin;

  const [step, setStep] = useState<Step>('scan');
  const [imei, setImei] = useState('');
  const [iccid, setIccid] = useState('');
  const [manualEntry, setManualEntry] = useState(false);
  const [simManualEntry, setSimManualEntry] = useState(false);
  const [scannerActive, setScannerActive] = useState(false);
  const [equipment, setEquipment] = useState<Equipment[]>([]);
  const [search, setSearch] = useState('');
  const [picked, setPicked] = useState<Equipment | null>(null);
  const [hours, setHours] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<any>(null);

  const scannerDivId = 'qr-reader';
  const html5QrCodeRef = useRef<Html5Qrcode | null>(null);
  const handledRef = useRef(false);

  // Equipment list
  useEffect(() => {
    if (step !== 'pick') return;
    setError(null);
    fetch(`${API_BASE_URL}/api/equipment`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('bfa_token')}` },
    })
      .then(async r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then(d => {
        const list = Array.isArray(d) ? d : (Array.isArray(d?.data) ? d.data : []);
        setEquipment(list);
        if (list.length === 0) setError('No equipment returned from server.');
      })
      .catch(e => setError(`Failed to load equipment: ${e.message}`));
  }, [step]);

  // Scanner lifecycle — IMEI (QR) on 'scan' step, ICCID (Code 128) on 'sim' step
  useEffect(() => {
    const isImeiStep = step === 'scan' && !manualEntry;
    const isSimStep = step === 'sim' && !simManualEntry;
    if (!isImeiStep && !isSimStep) return;

    handledRef.current = false;

    const stop = async () => {
      const inst = html5QrCodeRef.current;
      html5QrCodeRef.current = null;
      if (inst) {
        try { await inst.stop(); } catch {}
        try { await inst.clear(); } catch {}
      }
      setScannerActive(false);
    };

    const start = async () => {
      try {
        setError(null);
        const formats = isImeiStep
          ? [Html5QrcodeSupportedFormats.QR_CODE]
          : [Html5QrcodeSupportedFormats.CODE_128, Html5QrcodeSupportedFormats.CODE_39, Html5QrcodeSupportedFormats.QR_CODE];
        html5QrCodeRef.current = new Html5Qrcode(scannerDivId, {
          formatsToSupport: formats,
          verbose: false,
        });
        await html5QrCodeRef.current.start(
          { facingMode: 'environment' },
          { fps: 10, qrbox: isSimStep ? { width: 280, height: 100 } : { width: 250, height: 250 } },
          async (decoded) => {
            if (handledRef.current) return;
            const cleaned = String(decoded).trim().replace(/\D/g, '');
            if (isImeiStep) {
              if (/^\d{15}$/.test(cleaned)) {
                handledRef.current = true;
                await stop();
                setImei(cleaned);
                setStep('sim');
              } else {
                setError(`Scanned "${decoded}" — not a 15-digit IMEI. Keep scanning or enter manually.`);
              }
            } else if (isSimStep) {
              if (/^\d{19,20}$/.test(cleaned)) {
                handledRef.current = true;
                await stop();
                setIccid(cleaned);
                setStep('pick');
              } else {
                setError(`Scanned "${decoded}" — not a 19/20-digit ICCID. Keep scanning or enter manually.`);
              }
            }
          },
          () => { /* per-frame errors are noisy; ignore */ }
        );
        setScannerActive(true);
      } catch (e: any) {
        setError(`Camera failed: ${e.message || e}. Use manual entry.`);
      }
    };

    start();
    return () => { stop(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, manualEntry, simManualEntry]);

  if (!isSuperAdmin) {
    return (
      <div style={{ padding: 24 }}>
        <p>Super admin access required.</p>
        <button onClick={() => navigate('/')}>Home</button>
      </div>
    );
  }

  const submit = async () => {
    if (!picked) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/equipment/${picked.equipment_id}/link-tracker`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('bfa_token')}`,
        },
        body: JSON.stringify({ imei, install_hours: Number(hours), iccid: iccid || undefined }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || `HTTP ${res.status}`);
      setResult(data.data);
      setStep('done');
    } catch (e: any) {
      setError(e.message || 'Link failed');
    } finally {
      setSubmitting(false);
    }
  };

  const filtered = equipment.filter(e => {
    const q = search.toLowerCase();
    return !q ||
      (e.equipment_name || '').toLowerCase().includes(q) ||
      (e.bradanna_equipment_number || '').toLowerCase().includes(q);
  });

  return (
    <div style={{ padding: 16, maxWidth: 480, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <button onClick={() => navigate(-1)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
          <ArrowLeft size={24} />
        </button>
        <h2 style={{ margin: 0, fontSize: 18 }}>Link GPS Tracker</h2>
      </div>

      {/* Progress */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 20 }}>
        {['scan', 'sim', 'pick', 'hours', 'confirm'].map((s, i) => (
          <div key={s} style={{
            flex: 1, height: 4, borderRadius: 2,
            background: ['scan', 'sim', 'pick', 'hours', 'confirm'].indexOf(step) >= i ? '#3b82f6' : '#e5e7eb',
          }} />
        ))}
      </div>

      {error && (
        <div style={{ padding: 12, background: '#fee', border: '1px solid #f99', borderRadius: 8, marginBottom: 16, color: '#900', fontSize: 13 }}>
          {error}
        </div>
      )}

      {/* Step 1: Scan / manual IMEI */}
      {step === 'scan' && (
        <div>
          <p style={{ fontSize: 14, color: '#666', marginTop: 0 }}>
            Point your camera at the IMEI QR code on the tracker.
          </p>
          {!manualEntry ? (
            <>
              <div id={scannerDivId} style={{ width: '100%', minHeight: 280, background: '#000', borderRadius: 8 }} />
              <button
                onClick={() => setManualEntry(true)}
                style={{ marginTop: 12, padding: 12, width: '100%', background: '#f3f4f6', border: '1px solid #ddd', borderRadius: 8, cursor: 'pointer' }}
              >
                Type IMEI manually
              </button>
            </>
          ) : (
            <>
              <input
                type="tel"
                inputMode="numeric"
                pattern="[0-9]*"
                placeholder="15-digit IMEI"
                value={imei}
                onChange={e => setImei(e.target.value.replace(/\D/g, '').slice(0, 15))}
                style={{ width: '100%', padding: 14, fontSize: 18, border: '1px solid #ddd', borderRadius: 8, marginBottom: 12 }}
              />
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  onClick={() => { setManualEntry(false); setImei(''); }}
                  style={{ flex: 1, padding: 12, background: '#f3f4f6', border: '1px solid #ddd', borderRadius: 8, cursor: 'pointer' }}
                >
                  <Camera size={16} style={{ verticalAlign: 'middle' }} /> Back to scan
                </button>
                <button
                  onClick={() => setStep('sim')}
                  disabled={!/^\d{15}$/.test(imei)}
                  style={{ flex: 1, padding: 12, background: /^\d{15}$/.test(imei) ? '#3b82f6' : '#bbb', color: '#fff', border: 'none', borderRadius: 8, cursor: /^\d{15}$/.test(imei) ? 'pointer' : 'not-allowed' }}
                >
                  Next
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Step 2: Scan SIM (ICCID barcode) — optional */}
      {step === 'sim' && (
        <div>
          <p style={{ fontSize: 14, color: '#666', marginTop: 0 }}>
            Scan the SIM's barcode (the 19–20 digit ICCID). The SIM will be activated and renamed automatically.
          </p>
          {!simManualEntry ? (
            <>
              <div id={scannerDivId} style={{ width: '100%', minHeight: 220, background: '#000', borderRadius: 8 }} />
              <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                <button
                  onClick={() => setSimManualEntry(true)}
                  style={{ flex: 1, padding: 12, background: '#f3f4f6', border: '1px solid #ddd', borderRadius: 8, cursor: 'pointer' }}
                >
                  Type ICCID manually
                </button>
                <button
                  onClick={() => { setIccid(''); setStep('pick'); }}
                  style={{ flex: 1, padding: 12, background: '#fff', border: '1px solid #ddd', borderRadius: 8, cursor: 'pointer', color: '#666' }}
                  title="Add SIM later — link the tracker without activating now"
                >
                  Skip SIM
                </button>
              </div>
            </>
          ) : (
            <>
              <input
                type="tel"
                inputMode="numeric"
                pattern="[0-9]*"
                placeholder="19 or 20 digit ICCID"
                value={iccid}
                onChange={e => setIccid(e.target.value.replace(/\D/g, '').slice(0, 20))}
                style={{ width: '100%', padding: 14, fontSize: 18, border: '1px solid #ddd', borderRadius: 8, marginBottom: 12 }}
              />
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  onClick={() => { setSimManualEntry(false); setIccid(''); }}
                  style={{ flex: 1, padding: 12, background: '#f3f4f6', border: '1px solid #ddd', borderRadius: 8, cursor: 'pointer' }}
                >
                  <Camera size={16} style={{ verticalAlign: 'middle' }} /> Scan
                </button>
                <button
                  onClick={() => { setIccid(''); setStep('pick'); }}
                  style={{ flex: 1, padding: 12, background: '#fff', border: '1px solid #ddd', borderRadius: 8, cursor: 'pointer', color: '#666' }}
                >
                  Skip
                </button>
                <button
                  onClick={() => setStep('pick')}
                  disabled={!/^\d{19,20}$/.test(iccid)}
                  style={{ flex: 1, padding: 12, background: /^\d{19,20}$/.test(iccid) ? '#3b82f6' : '#bbb', color: '#fff', border: 'none', borderRadius: 8, cursor: /^\d{19,20}$/.test(iccid) ? 'pointer' : 'not-allowed' }}
                >
                  Next
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Step 3: Pick equipment */}
      {step === 'pick' && (
        <div>
          <p style={{ fontSize: 14, color: '#666', marginTop: 0 }}>IMEI: <code>{imei}</code></p>
          <input
            type="text"
            placeholder="Search by name or number"
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ width: '100%', padding: 12, fontSize: 16, border: '1px solid #ddd', borderRadius: 8, marginBottom: 12 }}
          />
          <div style={{ maxHeight: 380, overflowY: 'auto', border: '1px solid #eee', borderRadius: 8 }}>
            {filtered.map(e => (
              <button
                key={e.equipment_id}
                onClick={() => { setPicked(e); setHours(e.hours_last_serviced ? String(e.hours_last_serviced) : ''); setStep('hours'); }}
                style={{
                  display: 'block', width: '100%', textAlign: 'left',
                  padding: 12, background: '#fff', border: 'none', borderBottom: '1px solid #eee', cursor: 'pointer',
                }}
              >
                <div style={{ fontWeight: 600 }}>{e.equipment_name}</div>
                <div style={{ fontSize: 12, color: '#666' }}>
                  {e.bradanna_equipment_number || '—'}
                  {e.hours_last_serviced != null && ` · last service: ${e.hours_last_serviced} hrs`}
                </div>
              </button>
            ))}
            {filtered.length === 0 && (
              <div style={{ padding: 16, color: '#999', textAlign: 'center' }}>No equipment found</div>
            )}
          </div>
        </div>
      )}

      {/* Step 3: Hours */}
      {step === 'hours' && picked && (
        <div>
          <p style={{ fontSize: 14, color: '#666', marginTop: 0 }}>
            <strong>{picked.equipment_name}</strong> · IMEI <code>{imei}</code>
          </p>
          <label style={{ display: 'block', fontSize: 14, marginBottom: 8 }}>
            What does the hour meter show right now?
          </label>
          <input
            type="number"
            step="0.1"
            min="0"
            placeholder="e.g., 1234.5"
            value={hours}
            onChange={e => setHours(e.target.value)}
            style={{ width: '100%', padding: 14, fontSize: 18, border: '1px solid #ddd', borderRadius: 8, marginBottom: 12 }}
          />
          <p style={{ fontSize: 12, color: '#888' }}>
            Traccar will use this as the starting baseline. Going forward, engine hours = this number + runtime.
          </p>
          <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
            <button onClick={() => setStep('pick')} style={{ flex: 1, padding: 12, background: '#f3f4f6', border: '1px solid #ddd', borderRadius: 8, cursor: 'pointer' }}>
              Back
            </button>
            <button
              onClick={() => setStep('confirm')}
              disabled={!hours || Number(hours) < 0}
              style={{ flex: 1, padding: 12, background: hours && Number(hours) >= 0 ? '#3b82f6' : '#bbb', color: '#fff', border: 'none', borderRadius: 8, cursor: hours ? 'pointer' : 'not-allowed' }}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Step 4: Confirm */}
      {step === 'confirm' && picked && (
        <div>
          <h3 style={{ marginTop: 0 }}>Confirm</h3>
          <div style={{ background: '#f9fafb', padding: 16, borderRadius: 8, marginBottom: 16 }}>
            <div style={{ marginBottom: 8 }}><strong>Equipment:</strong> {picked.equipment_name}{picked.bradanna_equipment_number ? ` (${picked.bradanna_equipment_number})` : ''}</div>
            <div style={{ marginBottom: 8 }}><strong>Tracker IMEI:</strong> {imei}</div>
            <div style={{ marginBottom: 8 }}>
              <strong>SIM ICCID:</strong> {iccid ? <span style={{ color: '#10b981' }}>{iccid} (will activate)</span> : <span style={{ color: '#888', fontStyle: 'italic' }}>skipped — add later</span>}
            </div>
            <div><strong>Starting hours:</strong> {hours}</div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => setStep('hours')} disabled={submitting} style={{ flex: 1, padding: 12, background: '#f3f4f6', border: '1px solid #ddd', borderRadius: 8, cursor: 'pointer' }}>
              Back
            </button>
            <button
              onClick={submit}
              disabled={submitting}
              style={{ flex: 1, padding: 12, background: submitting ? '#bbb' : '#10b981', color: '#fff', border: 'none', borderRadius: 8, cursor: submitting ? 'wait' : 'pointer' }}
            >
              {submitting ? 'Linking...' : 'Link Tracker'}
            </button>
          </div>
        </div>
      )}

      {/* Step 5: Done */}
      {step === 'done' && result && (
        <div style={{ textAlign: 'center', padding: '32px 16px' }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>✅</div>
          <h3 style={{ marginTop: 0 }}>Tracker linked</h3>
          <p style={{ color: '#666', fontSize: 14 }}>
            IMEI <code>{result.imei}</code> is now linked to <strong>{picked?.equipment_name}</strong>.
            Starting hours: <strong>{result.install_hours}</strong>.
          </p>
          {result.sim_iccid && (
            <p style={{ color: result.sim_warning ? '#dc2626' : '#10b981', fontSize: 13 }}>
              {result.sim_warning
                ? `⚠️ ${result.sim_warning}`
                : `📶 SIM ${result.sim_iccid} activated on G3 Flat Rate plan.`}
            </p>
          )}
          <p style={{ color: '#888', fontSize: 12 }}>
            Engine hours will sync from Traccar nightly. Map view: open <a href={`http://52.230.251.24:8082/?deviceId=${result.traccar_device_id}`} target="_blank" rel="noreferrer">Traccar</a>.
          </p>
          <button
            onClick={() => navigate('/')}
            style={{ marginTop: 16, padding: 12, background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer' }}
          >
            Done
          </button>
        </div>
      )}
    </div>
  );
};

export default LinkTracker;
